/**
 * DuplicateReviewRow Component
 *
 * Displays a skipped duplicate alongside the existing matched transaction
 * in a side-by-side comparison layout for user review.
 *
 * Features:
 * - Checkbox for bulk selection
 * - Status badge (Unreviewed/Reviewed)
 * - Confidence badge (High confidence / Date tolerance match)
 * - Side-by-side transaction comparison
 * - Action buttons for marking reviewed or force importing
 */

import clsx from 'clsx';
import { Check, Plus, AlertCircle, ArrowLeftRight } from 'lucide-react';
import type { Duplicate } from '@/types/accounting';

export interface DuplicateReviewRowProps {
  /** The duplicate record to display */
  duplicate: Duplicate;
  /** Whether this row is selected for bulk actions */
  isSelected: boolean;
  /** Whether an action is currently being processed */
  isProcessing: boolean;
  /** Callback when selection changes */
  onSelectionChange: (id: number, selected: boolean) => void;
  /** Callback when user marks as reviewed */
  onMarkReviewed: (id: number) => void;
  /** Callback when user wants to force import */
  onForceImport: (id: number) => void;
}

/**
 * Format a date string for display
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * DuplicateReviewRow displays a duplicate match for user review.
 *
 * @example
 * <DuplicateReviewRow
 *   duplicate={duplicate}
 *   isSelected={false}
 *   isProcessing={false}
 *   onSelectionChange={handleSelection}
 *   onMarkReviewed={handleMarkReviewed}
 *   onForceImport={handleForceImport}
 * />
 */
export function DuplicateReviewRow({
  duplicate,
  isSelected,
  isProcessing,
  onSelectionChange,
  onMarkReviewed,
  onForceImport,
}: DuplicateReviewRowProps) {
  const isReviewed = duplicate.duplicateReviewed;
  const isHighConfidence = duplicate.matchConfidence === 'high';
  const hasDatTolerance = duplicate.dateTolerance > 0;

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectionChange(duplicate.id, e.target.checked);
  };

  const handleMarkReviewed = () => {
    if (!isProcessing) {
      onMarkReviewed(duplicate.id);
    }
  };

  const handleForceImport = () => {
    if (!isProcessing) {
      onForceImport(duplicate.id);
    }
  };

  return (
    <div
      className={clsx(
        'bg-white border rounded-xl overflow-hidden',
        'shadow-sm',
        isSelected ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200',
        isReviewed && 'opacity-70'
      )}
      data-testid="duplicate-review-row"
      data-duplicate-id={duplicate.id}
    >
      {/* Row Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            disabled={isProcessing}
            className="w-[18px] h-[18px] rounded cursor-pointer accent-blue-500"
            data-testid="duplicate-checkbox"
          />
          <span
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium',
              isReviewed
                ? 'bg-green-100 text-green-800'
                : 'bg-amber-100 text-amber-800'
            )}
            data-testid="status-badge"
          >
            {isReviewed ? (
              <>
                <Check size={12} />
                Reviewed
              </>
            ) : (
              <>
                <AlertCircle size={12} />
                Unreviewed
              </>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'px-2 py-1 rounded-md text-xs font-medium',
              isHighConfidence && !hasDatTolerance
                ? 'bg-blue-100 text-blue-800'
                : 'bg-amber-100 text-amber-800'
            )}
            data-testid="confidence-badge"
          >
            {isHighConfidence && !hasDatTolerance
              ? 'High confidence match'
              : `Date tolerance match (+${duplicate.dateTolerance} day${duplicate.dateTolerance !== 1 ? 's' : ''})`}
          </span>
        </div>
      </div>

      {/* Comparison Grid */}
      <div className="grid grid-cols-[1fr_auto_1fr]" data-testid="comparison-grid">
        {/* Skipped Transaction Card */}
        <div
          className={clsx(
            'p-4',
            isReviewed ? 'bg-gray-50' : 'bg-amber-50'
          )}
          style={{ backgroundColor: isReviewed ? undefined : '#fffbeb' }}
          data-testid="skipped-transaction-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-amber-200 text-amber-800"
              data-testid="skipped-label"
            >
              New (Skipped)
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              {duplicate.skippedTransaction.source}
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-gray-500">Date</span>
              <span
                className={clsx(
                  'text-sm font-medium',
                  hasDatTolerance ? 'text-amber-700' : 'text-gray-900'
                )}
                data-testid="skipped-date"
              >
                {formatDate(duplicate.skippedTransaction.date)}
              </span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-gray-500">Amount</span>
              <span
                className={clsx(
                  'text-sm font-medium font-mono',
                  duplicate.skippedTransaction.amountCents < 0 ? 'text-red-600' : 'text-green-600'
                )}
                data-testid="skipped-amount"
              >
                {duplicate.skippedTransaction.amountDisplay}
              </span>
            </div>
            <div>
              <span className="text-xs text-gray-500">Description</span>
              <div
                className="mt-1 text-sm text-gray-700 bg-gray-50 p-2 rounded-md"
                data-testid="skipped-description"
              >
                {duplicate.skippedTransaction.description}
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Divider */}
        <div
          className="flex flex-col items-center justify-center px-3 py-4 bg-gray-100 border-x border-gray-200"
          data-testid="comparison-divider"
        >
          <div
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center mb-2',
              hasDatTolerance ? 'bg-amber-100' : 'bg-blue-100'
            )}
          >
            <ArrowLeftRight
              size={16}
              className={hasDatTolerance ? 'text-amber-700' : 'text-blue-600'}
            />
          </div>
          <span
            className="text-[11px] text-gray-500 uppercase tracking-wider"
            style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              transform: 'rotate(180deg)',
            }}
          >
            MATCHES
          </span>
        </div>

        {/* Existing Transaction Card */}
        <div
          className="p-4"
          style={{ backgroundColor: '#f0fdf4' }}
          data-testid="existing-transaction-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-green-200 text-green-800"
              data-testid="existing-label"
            >
              Existing
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              {duplicate.existingTransaction.source}
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-gray-500">Date</span>
              <span
                className={clsx(
                  'text-sm font-medium',
                  hasDatTolerance ? 'text-amber-700' : 'text-gray-900'
                )}
                data-testid="existing-date"
              >
                {formatDate(duplicate.existingTransaction.date)}
              </span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-gray-500">Amount</span>
              <span
                className={clsx(
                  'text-sm font-medium font-mono',
                  duplicate.existingTransaction.amountCents < 0 ? 'text-red-600' : 'text-green-600'
                )}
                data-testid="existing-amount"
              >
                {duplicate.existingTransaction.amountDisplay}
              </span>
            </div>
            <div>
              <span className="text-xs text-gray-500">Description</span>
              <div
                className="mt-1 text-sm text-gray-700 bg-gray-50 p-2 rounded-md"
                data-testid="existing-description"
              >
                {duplicate.existingTransaction.description}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row Actions */}
      <div className="flex justify-end gap-2 px-4 py-3 bg-gray-50 border-t border-gray-200">
        <button
          type="button"
          onClick={handleMarkReviewed}
          disabled={isProcessing || isReviewed}
          className={clsx(
            'inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium',
            'bg-white border border-gray-300 text-gray-700',
            'hover:bg-gray-50 hover:border-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors'
          )}
          data-testid="mark-reviewed-button"
        >
          <Check size={14} />
          Mark as Reviewed
        </button>
        <button
          type="button"
          onClick={handleForceImport}
          disabled={isProcessing}
          className={clsx(
            'inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium',
            'bg-blue-500 text-white',
            'hover:bg-blue-600',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors'
          )}
          data-testid="force-import-button"
        >
          <Plus size={14} />
          Import Anyway
        </button>
      </div>
    </div>
  );
}

export default DuplicateReviewRow;
